import { getHomeHostHint } from '#pplx/frontend/apps/ask/auth/homeHostHint.js';
import { resolveHomeHostRedirect } from '#pplx/frontend/apps/ask/auth/resolveHomeHostRedirect.js';
/**
 * Hosts the hint is allowed to route to.
 *
 * Deliberately narrower than resolveHomeHostRedirect's isRecognizedPerplexityHost,
 * which accepts *any* `*.perplexity.ai` / `*.perplexity.com` host. That check is
 * the right breadth for a callbackUrl supplied by our own server, but not for the
 * hint: the hint is a Domain=.perplexity.ai cookie, so anything able to set a
 * cookie on the registrable domain — XSS on any sibling subdomain, a subdomain
 * takeover, or the user themselves — could otherwise steer production www traffic
 * to a host of its choosing, with path and query preserved.
 *
 * Add an entry here (not to isRecognizedPerplexityHost) when a per-org custom
 * subdomain such as nvidia.perplexity.ai ships; authservice derives the hint value
 * from the home_host claim, so the two lists must stay in step.
 */
const ALLOWED_HINT_HOSTS = new Set(['enterprise.perplexity.ai']);
/**
 * Resolves where an *anonymous* browser on the consumer host to the enterprise host it
 * previously signed in on, using the domain-scoped `pplx.home-host` hint cookie.
 *
 * Why this exists rather than relying on HomeHostRedirect: an enterprise user who
 * signs in via WorkOS SSO never authenticates on www at all — the pre-auth redirect
 * in redirectToEnterpriseOrigin.ts fires before any credential is presented, so www
 * never mints a session and has no `home_host` claim to route them with. Every later
 * visit to www therefore looks anonymous and re-prompts sign-in, forever. The hint is
 * the only signal available in that state.
 *
 * Precedence: only consult the hint once the session has resolved to anonymous. A
 * resolved session's home_host claim is authoritative and the hint is not, and acting
 * before the session resolves would bounce a user who does have a www session. The
 * bootstrap caller (redirectToHomeHostBeforeBoot) enforces that ordering.
 *
 * The hint carries NO authority. It is a hostname, not a credential, and anything
 * able to set a cookie on .perplexity.ai can forge it. It may only influence
 * routing. Two checks make that safe: the value must be in ALLOWED_HINT_HOSTS, and
 * resolveHomeHostRedirect independently rejects a non-production source origin and
 * anything not single-slash-rooted. A forged value yields a no-op, never a
 * redirect off the enterprise host.
 *
 * A stale hint (session since expired) is acceptable and intentional: the user lands
 * on the enterprise sign-in page, which is where an enterprise user should
 * authenticate anyway, and the fresh mint rewrites the hint.
 *
 * Safety mirrors HomeHostRedirect, whose two SEV causes this must not reintroduce:
 * production consumer origins only (via resolveHomeHostRedirect's explicit allowlist,
 * never env.isProduction, which misclassifies staging); not feature-flagged (the former
 * `enterprise-home-host-hint-redirect-enabled` kill switch was fully launched and
 * removed, so bootstrap routing takes no Eppo dependency); the bootstrap caller
 * catches any failure so it degrades to "routing off" rather than a white screen.
 *
 * No client-type gate is needed here, and that is a deliberate consequence of where the
 * write is gated. authservice writes a hint only for web browsers and for Comet builds
 * at or above enterprise-origin-min-comet-version; native clients switch hosts by a
 * different mechanism entirely and older Comet builds have their login state corrupted
 * by a cross-host redirect. Because a client that must not be bounced never receives a
 * hint, this function can route anything that has one. Keep it that way: re-deriving
 * the Comet version check here would put the same rule in a third language.
 */
export function resolveHomeHostHintRedirect({ currentOrigin, pathname, search, }) {
    // The cookie read comes first so a browser with no hint (every consumer user) does
    // no further work.
    const hintedHost = getHomeHostHint();
    if (!hintedHost || !ALLOWED_HINT_HOSTS.has(hintedHost)) {
        return null;
    }
    return resolveHomeHostRedirect({
        homeHost: `https://${hintedHost}`,
        currentOrigin,
        pathname,
        search,
    });
}
//# sourceMappingURL=homeHostHintRedirect.js.map